import { Alert, Button, Tooltip } from "antd";
import cx from "classnames";
import ItemList from "components/Utility/ItemList";
import dayjs from "dayjs";
import { LONG_DATE_TIME_FORMAT } from "fixtures/globalConstants";
import usePostRefresh from "hooks/usePostRefresh";
import { useMemo, useState } from "react";
import { LuRefreshCw } from "react-icons/lu";
import { Link } from "react-router-dom";
import { Post } from "store/posts/postConstants";
import groupPublishLinksByState, {
  PublishResults,
} from "utils/groupPublishLinksByState";
import PublishErrorModal from "../../components/Utility/PublishErrorModal";

type PublishedLinksProp = { results: PublishResults };

// TODO: REFACTOR
const PublishDetailsAlert = ({ post }: { post: Post }) => {
  const { isRefreshing, triggerManualRefresh } = usePostRefresh({ post });

  const links = useMemo(
    () => groupPublishLinksByState(post.publish_results),
    [post]
  );

  const pendingSocialsText = useMemo(() => {
    return (
      <div>
        Publishing to <PendingSocials results={links} /> is in progress.
        <Tooltip title="Refresh status">
          <Button
            icon={<LuRefreshCw className="relative top-0.5" />}
            onClick={triggerManualRefresh}
            disabled={isRefreshing}
            className={cx("refresh-post-btn disabled:!text-antd-colorInfo", {
              "!pointer-events-none refresh-post-btn--loading": isRefreshing,
            })}
            type="link"
          />
        </Tooltip>
      </div>
    );
  }, [links, post, isRefreshing]);

  return (
    <div>
      {links.successLinks.count > 0 && (
        <Alert
          showIcon
          className="rounded-b-none"
          type="success"
          message={
            <div>
              Published on{" "}
              {dayjs
                .utc(post.published_at)
                .local()
                .format(LONG_DATE_TIME_FORMAT)}
              {<PublishedLinks results={links} />}
            </div>
          }
        />
      )}
      {links.failedLinks.count > 0 && (
        <Alert
          showIcon
          className="rounded-b-none"
          type="error"
          message={
            <div>
              Failed to publish to <FailedLinks results={links} post={post} />
            </div>
          }
        />
      )}
      {links.pendingLinks.count > 0 && (
        <Alert
          showIcon
          className="rounded-b-none"
          type="info"
          message={pendingSocialsText}
        />
      )}
    </div>
  );
};

const FailedLinks = ({
  results: { failedLinks },
  post,
}: {
  post: Post;
} & PublishedLinksProp) => {
  const [publishError, setPublishError] = useState<{ name?: string }>({
    name: "",
  });

  if (!failedLinks.count) {
    return null;
  }

  return (
    <>
      <ItemList
        items={failedLinks.data}
        renderItem={(name, error) => (
          <>
            {name}{" "}
            <span
              onClick={() => setPublishError(error)}
              className="cursor-pointer text-antd-colorInfo hover:underline"
            >
              (see why)
            </span>
          </>
        )}
      />
      <PublishErrorModal
        close={() => setPublishError({ name: "" })}
        key={publishError.name}
        error={publishError}
        post={post}
      />
    </>
  );
};

const PublishedLinks = ({ results: { successLinks } }: PublishedLinksProp) => {
  if (!successLinks.count) {
    return null;
  }

  return (
    <>
      {" to "}
      <ItemList
        items={successLinks.data}
        renderItem={(name, link) => (
          <Link
            className="text-antd-colorInfo hover:underline"
            target="_blank"
            to={link}
          >
            {name}
          </Link>
        )}
      />
    </>
  );
};

const PendingSocials = ({ results: { pendingLinks } }: PublishedLinksProp) => {
  if (!pendingLinks.count) {
    return null;
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const hours = Math.floor(time / 3600);

    if (time <= 60) {
      return `${time} seconds`;
    } else if (time <= 3600) {
      return `${minutes} minutes`;
    } else {
      return `${hours} hours`;
    }
  };

  return (
    <ItemList
      items={pendingLinks.data}
      renderItem={(name, { created_at, timeout, state }) => {
        const createdAt = new Date(created_at ?? 0).getTime();
        return (
          <Tooltip
            title={() => (
              <span>
                {`Publish pending for ${formatTime(
                  Math.ceil((Date.now() - createdAt) / 1000)
                )}.`}{" "}
                {state === "pending"
                  ? `It can take up to ${formatTime(timeout ?? 0)}.`
                  : ""}
              </span>
            )}
          >
            <span className="pending-social">{name}</span>
          </Tooltip>
        );
      }}
    />
  );
};

export default PublishDetailsAlert;
